import { useContext } from 'react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json';
import NewTaskModal from './NewTaskModal';
import { decoupledSave } from '../../services/api';
import { notify } from '../../notices';

/**
 * TaskCreateModal — l'enveloppe reactive-json de la popin de création de tâche.
 *
 * MÊME popin que dans un projet : elle rend `NewTaskModal`, celle du Kanban,
 * sans la redessiner. Ce fichier n'existe que parce que `NewTaskModal` est un
 * composant à CALLBACKS (`isOpen` / `onClose` / `onSave`) : reactive-json passe
 * des valeurs, jamais des fonctions, donc il faut un adaptateur. C'est le même
 * montage que `GroupMembersPanel` pour le trombinoscope partagé — la règle
 * maison : on réutilise le composant générique, on ne duplique pas son écran.
 *
 * L'adaptateur fait trois choses, et rien de plus :
 *   1. il traduit les props YAML en callbacks ;
 *   2. il POSTe la tâche par l'API decoupled — `linkedContent` pré-rempli avec
 *      la fiche courante, le reste (état initial, projet) étant posé par le
 *      serveur, jamais deviné ici ;
 *   3. il relit la collection. Les actions reactive-json évaluent bien une URL
 *      tenue en donnée, mais rien ici ne peut CONSTRUIRE une URL contenant
 *      l'identifiant de la fiche — les props, elles, sont évaluées.
 *
 * Props :
 *   - isOpen        : booléen d'ouverture (chemin évalué).
 *   - openPath      : le même chemin, brut, pour le refermer.
 *   - linkedId      : la fiche à laquelle rattacher la tâche (field_linked_content).
 *   - defaultTypeName : nom du type de tâche imposé (facultatif). Absent, la
 *                     popin présélectionne le premier type autorisé du projet.
 *   - typeOptions, users : collections decoupled déjà chargées.
 *   - evolutionTypeOptions, returnTypeOptions, tagOptions : taxonomies annexes
 *                     du formulaire, collections decoupled { id, name }.
 *   - tasksPath     : où réécrire la liste rafraîchie (fiche uniquement).
 *   - projectId     : projet CIBLE explicite (signalement) ; vide sur une fiche,
 *                     où le serveur dérive le projet.
 *   - reportContext : contexte technique déjà sérialisé en JSON, transporté tel quel.
 *   - description / transcriptFid : contenu pré-rempli venu du signalement — la
 *     description rédigée par l'assistant du builder et l'historique de la
 *     conversation, déjà téléversé.
 *   - savedPath     : chemin passé à true après un enregistrement réussi.
 *   - captureFid    : identifiant du fichier de capture déjà téléversé, joint
 *                     à la tâche au même titre qu'une pièce jointe manuelle.
 *   - inline        : rend le formulaire DANS la page au lieu d'une popin
 *                     (même contenu, enveloppe différente).
 */
export const TaskCreateModal = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const ev = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext });

  const isOpen = Boolean(ev(props.isOpen));
  const openPath = String(props.openPath ?? '').replace(/^~~?\./, '');
  const tasksPath = String(props.tasksPath ?? 'tasks').replace(/^~~?\./, '');
  const linkedId = ev(props.linkedId);
  const defaultTypeName = props.defaultTypeName ?? '';
  // Projet CIBLE, explicite. Sur une fiche il reste vide (le serveur dérive), sur
  // /report il porte le choix du signalant. Le serveur reste seul juge : référencer
  // un projet qu'on ne peut pas voir est refusé en 422 par la contrainte
  // ValidReference, avant même le presave.
  const projectId = ev(props.projectId);
  // Contexte technique du signalement, déjà sérialisé en JSON par l'appelant.
  // Opaque ici : ce composant le transporte, il ne l'interprète pas.
  const reportContext = ev(props.reportContext);
  // Identifiant du fichier de capture, déjà téléversé par l'appelant.
  const captureFid = ev(props.captureFid);
  // Historique de conversation joint au signalement (assistant du builder), et
  // description qu'il a pré-rédigée. Tous deux facultatifs : un signalement
  // ordinaire n'en a pas.
  const transcriptFid = ev(props.transcriptFid);
  const initialDescription = ev(props.description);

  const rawTypes = ev(props.typeOptions) || [];
  const rawUsers = ev(props.users) || [];
  // Taxonomies annexes du formulaire, transmises TELLES QUELLES : le processeur
  // `taxonomyToOptionsProcessor` les a déjà converties en { value, label }, la
  // forme exacte qu'attend NewTaskModal. Les remapper depuis { id, name } — ce
  // qu'elles ne sont plus à ce stade — ne produisait que des libellés vides.
  const evolutionTypeOptions = ev(props.evolutionTypeOptions) || [];
  const returnTypeOptions = ev(props.returnTypeOptions) || [];
  const tagOptions = ev(props.tagOptions) || [];
  // Projet CRM de l'utilisateur, résolu par /api/user/info (même règle que la
  // dérivation côté serveur). `enabledTaskTypes` en donne les types AUTORISÉS,
  // par NOM — c'est la forme qu'expose le display decoupled du projet.
  // Deux façons de désigner le projet dont on tire les types AUTORISÉS :
  //   - `crmProject` : l'objet déjà résolu (usage historique, depuis une fiche) ;
  //   - `projects` + `projectId` : la collection et le choix courant, quand le
  //     projet se choisit à l'écran. La résolution se fait ici parce que c'est un
  //     `find` dans une collection — inexprimable en YAML sans processor, alors
  //     que le composant tient déjà la logique juste en dessous.
  const rawProjects = ev(props.projects) || [];
  const crmProject = ev(props.crmProject)
    || (projectId
      ? (rawProjects.map((x) => (x && x.data ? x.data : x))
        .find((x) => String(x?.id) === String(projectId)) || null)
      : null);
  // Types autorisés par le projet, dans le contrat de NewTaskModal : une TABLE
  // { "Nom du type": true }. `mapProject` la fournit déjà sous cette forme ; on
  // tolère quand même une liste de noms, car un appelant qui passerait
  // `crmProject` brut (sortie decoupled) en fournirait une.
  const rawEnabled = crmProject?.enabledTaskTypes;
  const enabledTaskTypes = Array.isArray(rawEnabled)
    ? Object.fromEntries(rawEnabled.map((name) => [name, true]))
    : ((rawEnabled && typeof rawEnabled === 'object') ? rawEnabled : {});

  // Les collections decoupled arrivent en {data:{…}} ou à plat selon le display :
  // on tolère les deux, comme le fait la politique du board.
  const dataOf = (x) => (x && x.data ? x.data : x) || {};
  // AUCUN filtrage ici : c'est NewTaskModal qui restreint, à partir de
  // `enabledTaskTypes`. Filtrer aussi de ce côté dupliquait la règle tout en
  // étant MOINS juste — sa version écarte en plus les sous-types (parentId) et
  // conserve toujours la valeur courante pour qu'un <select> contrôlé ne se vide
  // jamais. Une seule règle, celle du Kanban.
  const typeOptions = rawTypes
    .map((t) => ({ value: String(dataOf(t).id), label: dataOf(t).name, data: dataOf(t) }));


  const users = rawUsers.map((u) => ({ id: dataOf(u).id, name: dataOf(u).name }));
  const defaultType = typeOptions.find((o) => o.label === defaultTypeName)?.value ?? '';

  const close = () => { if (openPath) globalDataContext.updateData(false, openPath); };

  // `options.createAnother` : la case « Créer un autre ticket » de la popin.
  // Quand elle est cochée, la popin se réinitialise et RESTE ouverte — on ne
  // la referme donc pas ici, on se contente de rafraîchir la liste.
  const handleSave = async (taskData, options = {}) => {
    // L'assigné revient en NOM (saisie libre du composant) : on le résout contre
    // les comptes connus, jamais en aveugle.
    const assignee = users.find((u) => u.name === taskData.assignee)?.id ?? null;

    // Un type est OBLIGATOIRE, et le refus est ici plutôt que dans NewTaskModal
    // pour ne pas toucher au Kanban, qui en impose toujours un. Sans type, la
    // tâche n'a pas de workflow : orbit_task_node_presave ne peut lui donner
    // aucun état initial, et une tâche sans état n'apparaît sur aucun board.
    // Mieux vaut un refus lisible qu'un ticket créé puis invisible.
    const resolvedType = taskData.type || defaultType;
    if (!resolvedType) {
      notify({ type: 'error', message: 'Choisissez un type de tâche.' });
      return;
    }

    const payload = {
      title: taskData.title,
      type: resolvedType,
      assignee,
      description: taskData.description || null,
      tags: taskData.tags || [],
      linkedContent: linkedId ? [linkedId] : [],
      // Échéance (field_due_date, date-only) : la popin rend déjà du YYYY-MM-DD.
      dueDate: taskData.dueDate || null,
      // Pièces jointes : NewTaskModal a déjà téléversé les fichiers et n'en
      // renvoie que les identifiants. Les omettre laisse, en silence, des
      // fichiers orphelins sur le serveur et une tâche sans ses pièces.
      // La capture rejoint les pièces jointes saisies à la main : même champ,
      // même traitement côté serveur. Elle est en tête, c'est la pièce que l'on
      // regarde en premier sur un signalement.
      // La capture d'abord — c'est la pièce qu'on regarde en premier sur un
      // signalement —, puis l'historique, puis les fichiers saisis à la main.
      attachedFiles: [
        ...(captureFid ? [captureFid] : []),
        ...(transcriptFid ? [transcriptFid] : []),
        ...(taskData.attachedFiles || []),
      ],
    };
    // Omis quand vides : field_project absent laisse la dérivation serveur
    // opérer, et un reportContext vide n'a pas à écraser le champ.
    if (projectId) payload.projectId = projectId;
    if (reportContext) payload.reportContext = reportContext;
    // priorité / complexité OPTIONNELLES : omises si non définies (envoyer 0
    // viole le min:1 du champ et rend un 422).
    const p = parseInt(taskData.rating, 10);
    if (Number.isInteger(p) && p >= 1) payload.priority = p;
    const c = parseInt(taskData.complexity, 10);
    if (Number.isInteger(c) && c >= 1) payload.complexity = c;

    try {
      await decoupledSave('node', 'task', payload);
      // La relecture n'a de sens qu'adossée à une fiche : c'est SA collection
      // qu'on rafraîchit. Sans fiche liée (signalement autonome), il n'y a
      // aucune liste à l'écran, et l'URL filtrée serait vide de sens.
      if (linkedId) {
        const url = '/decoupled-api/node/task/collection?display=decoupled_card'
          + '&sort=-field_priority,title'
          + `&filter[0][f]=field_linked_content&filter[0][v]=${encodeURIComponent(linkedId)}`;
        const fresh = await fetch(url, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null));
        if (Array.isArray(fresh)) globalDataContext.updateData(fresh, tasksPath);
      }
      // « Créer un autre ticket » : la popin reste ouverte et se réinitialise
      // elle-même. On ne signale donc PAS le succès au YAML appelant, sinon son
      // écran de confirmation remplacerait le formulaire qu'on veut réutiliser.
      if (options.createAnother) return;
      // Drapeau de succès, à la main du YAML appelant (message, redirection…).
      const savedPath = String(props.savedPath ?? '').replace(/^~~?\./, '');
      if (savedPath) globalDataContext.updateData(true, savedPath);
      close();
    }
    catch (err) {
      notify({ type: 'error', message: String(err?.message || 'Création refusée.') });
    }
  };

  return (
    <NewTaskModal
      isOpen={isOpen}
      onClose={close}
      onSave={handleSave}
      inline={Boolean(ev(props.inline))}
      taskType={defaultType}
      users={users}
      typeOptions={typeOptions}
      enabledTaskTypes={enabledTaskTypes}
      initialDescription={initialDescription}
      tagOptions={tagOptions}
      returnTypeOptions={returnTypeOptions}
      evolutionTypeOptions={evolutionTypeOptions}
    />
  );
};

export default TaskCreateModal;
